<script lang="ts">
	import MenuItem from "@components/navigation/MenuItem.svelte";
	import { useGroup } from "@components/utils/group.store";
	import ClearIcon from "@svg/ClearIcon.svelte";
	import Contact from "@svg/Contact.svelte";
	import FavoriteIcon from "@svg/FavoriteIcon.svelte";
	import Home from "@svg/Home.svelte";
	import Movie from "@svg/Movie.svelte";
	import Person from "@svg/Person.svelte";
	import Policy from "@svg/Policy.svelte";
	import Terms from "@svg/Terms.svelte";
	import { Dialog } from "bits-ui";
	import { onMount } from "svelte";
	import { Drawer } from "vaul-svelte";

	// Use a reactive variable to track screen size
	let large = $state(false);

	// Function to check if screen is large
	function checkScreenSize() {
		large = window.matchMedia("(min-width: 1024px)").matches;
	}

	// Add event listeners for screen resize and page navigation
	onMount(() => {
		checkScreenSize();
		window.addEventListener("resize", checkScreenSize);

		// Create a named function for the event handler so we can properly remove it
		const handlePageSwap = () => {
			close();
			setValue(null);
		};

		// Add event listener for Astro page navigation completion
		document.addEventListener("astro:after-swap", handlePageSwap);

		return () => {
			window.removeEventListener("resize", checkScreenSize);
			document.removeEventListener("astro:after-swap", handlePageSwap);
		};
	});

	const { pathname = null } = $props<{ pathname?: string | null }>();
	const { selected, setValue } = useGroup<boolean>("menuDrawer");

	let drawerTriggerRef: HTMLElement | null = $state(null);
	let dialogTriggerRef: HTMLElement | null = $state(null);
	let drawerCloseRef: HTMLElement | null = $state(null);
	let dialogCloseRef: HTMLElement | null = $state(null);

	function open() {
		if (large) {
			dialogTriggerRef?.click();
		} else {
			drawerTriggerRef?.click();
		}
	}

	function close() {
		if (large) {
			dialogCloseRef?.click();
		} else {
			drawerCloseRef?.click();
		}
	}

	selected.subscribe((value) => {
		if (value) {
			setTimeout(() => {
				open();
			}, 0);
		}
	});

	interface MenuItemType {
		href: string;
		icon: any;
		label: string;
		cssClass?: string;
	}

	const menuItems: MenuItemType[] = [
		{ href: "/", icon: Home, label: "Home" },
		{ href: "/profile", icon: Person, label: "Profiel" },
		{ href: "/videos", icon: Movie, label: "Videos" },
		{ href: "/favourites", icon: FavoriteIcon, label: "Favorieten" },
		{
			href: "/contact",
			icon: Contact,
			label: "Contact",
			cssClass: "lg:mt-auto mt-4"
		},
		{ href: "/privacy-policy", icon: Policy, label: "Privacy policy" },
		{ href: "/terms-of-service", icon: Terms, label: "Terms of service" }
	];

	function isActive(path: string): boolean {
		if (path === "/" && pathname === "/") return true;
		if (path !== "/" && pathname?.includes(path)) return true;
		return false;
	}
</script>

{#snippet content()}
	<div class="flex h-full flex-col">
		<button class="p-2 lg:hidden" onclick={close}>
			<ClearIcon />
		</button>

		<div class="flex h-full flex-col pt-3">
			{#each menuItems as { href, icon, label, cssClass }}
				<MenuItem {href} {icon} {label} {cssClass} isActive={isActive(href)} />
			{/each}
		</div>
	</div>
{/snippet}

{#if !large}
	<Drawer.Root
		onClose={() => {
			setTimeout(() => {
				setValue(null);
			}, 200);
		}}
		direction="left"
	>
		<Drawer.Trigger class="hidden">
			<div bind:this={drawerTriggerRef}></div>
		</Drawer.Trigger>
		<Drawer.Close class="hidden">
			<div bind:this={drawerCloseRef}></div>
		</Drawer.Close>

		<Drawer.Portal>
			<Drawer.Overlay class="fixed inset-0 z-[60] bg-black/80" />

			<Drawer.Content
				class="fixed left-0 top-0 z-[70] flex h-full w-[80%] max-w-[300px] flex-col bg-white"
			>
				{#if $selected}
					{@render content()}
				{/if}
			</Drawer.Content>
		</Drawer.Portal>
	</Drawer.Root>
{:else}
	<Dialog.Root
		onOpenChange={(open) => {
			if (!open) {
				setValue(null);
			}
		}}
	>
		<Dialog.Trigger class="hidden">
			<div bind:this={dialogTriggerRef}></div>
		</Dialog.Trigger>
		<Dialog.Close class="hidden" bind:ref={dialogCloseRef}></Dialog.Close>
		<Dialog.Portal>
			<Dialog.Overlay class="fixed inset-0 z-[60] bg-black/50 pl-80" />

			<Dialog.Content
				class="fixed left-[50%] top-[50%] z-[60] max-h-[85vh] w-[90vw] max-w-[800px] translate-x-[-50%] translate-y-[-50%] rounded-lg bg-white p-6 shadow-lg xl:left-[calc(50%+var(--spacing)*40)]"
			>
				{#if $selected}
					{@render content()}
				{/if}
			</Dialog.Content>
		</Dialog.Portal>
	</Dialog.Root>
{/if}
